import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { FinancialPeriod, RevenueStream, RevenueTarget } from '../types/models';

export interface CreateFinancialPeriodData {
  name: string;
  start_date: string;
  end_date: string;
  period_type?: string;
  is_active?: boolean;
}

export interface CreateRevenueStreamData {
  financial_period_id: number;
  name: string;
  description?: string;
  target_amount?: number;
  services?: number[];
}

export interface CreateRevenueTargetData {
  revenue_stream_id: number;
  subcounty_id?: number;
  ward_id?: number;
  zone_id?: number;
  target_amount: number;
}

export interface RevenueStreamSummary {
  stream: RevenueStream;
  total_collected: number;
  total_target: number;
  percentage_achieved: number;
  services_count: number;
  targets_count: number;
}

class RevenueService {
  private periodBaseUrl = '/admin/county/financial-periods';
  private streamBaseUrl = '/admin/county/revenue-streams';
  private targetBaseUrl = '/admin/county/revenue-targets';

  // Financial Period endpoints
  async getFinancialPeriods(filters?: { page?: number; per_page?: number; search?: string }): Promise<ApiResponse<PaginatedResponse<FinancialPeriod>>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.search) params.append('search', filters.search);
    const queryString = params.toString();
    return apiClient.get<PaginatedResponse<FinancialPeriod>>(`${this.periodBaseUrl}${queryString ? `?${queryString}` : ''}`);
  }

  async getFinancialPeriod(id: number): Promise<ApiResponse<FinancialPeriod>> {
    return apiClient.get<FinancialPeriod>(`${this.periodBaseUrl}/${id}`);
  }

  async createFinancialPeriod(data: CreateFinancialPeriodData): Promise<ApiResponse<FinancialPeriod>> {
    return apiClient.post<FinancialPeriod>(this.periodBaseUrl, data);
  }

  async updateFinancialPeriod(id: number, data: Partial<CreateFinancialPeriodData>): Promise<ApiResponse<FinancialPeriod>> {
    return apiClient.put<FinancialPeriod>(`${this.periodBaseUrl}/${id}`, data);
  }

  async deleteFinancialPeriod(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.periodBaseUrl}/${id}`);
  }

  async activateFinancialPeriod(id: number): Promise<ApiResponse<FinancialPeriod>> {
    return apiClient.post<FinancialPeriod>(`${this.periodBaseUrl}/${id}/activate`);
  }

  async deactivateFinancialPeriod(id: number): Promise<ApiResponse<FinancialPeriod>> {
    return apiClient.post<FinancialPeriod>(`${this.periodBaseUrl}/${id}/deactivate`);
  }

  async getClosureSummary(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.periodBaseUrl}/${id}/closure-summary`);
  }

  async getActivePeriod(): Promise<ApiResponse<FinancialPeriod | null>> {
    return apiClient.get<FinancialPeriod | null>(`${this.periodBaseUrl}/active`);
  }

  // Revenue Stream endpoints
  async getRevenueStreams(periodId?: number, page?: number): Promise<ApiResponse<PaginatedResponse<RevenueStream>>> {
    const params = new URLSearchParams();
    if (periodId) params.append('period_id', periodId.toString());
    if (page) params.append('page', page.toString());
    return apiClient.get<PaginatedResponse<RevenueStream>>(`${this.streamBaseUrl}?${params.toString()}`);
  }

  async getRevenueStream(id: number): Promise<ApiResponse<RevenueStream>> {
    return apiClient.get<RevenueStream>(`${this.streamBaseUrl}/${id}`);
  }

  async createRevenueStream(data: CreateRevenueStreamData): Promise<ApiResponse<RevenueStream>> {
    return apiClient.post<RevenueStream>(this.streamBaseUrl, data);
  }

  async updateRevenueStream(id: number, data: Partial<CreateRevenueStreamData>): Promise<ApiResponse<RevenueStream>> {
    return apiClient.put<RevenueStream>(`${this.streamBaseUrl}/${id}`, data);
  }

  async deleteRevenueStream(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.streamBaseUrl}/${id}`);
  }

  async assignServices(streamId: number, serviceIds: number[]): Promise<ApiResponse<RevenueStream>> {
    return apiClient.post<RevenueStream>(`${this.streamBaseUrl}/${streamId}/services`, { service_ids: serviceIds });
  }

  async removeService(streamId: number, serviceId: number): Promise<ApiResponse<RevenueStream>> {
    return apiClient.delete(`${this.streamBaseUrl}/${streamId}/services/${serviceId}`);
  }

  async getStreamSummary(streamId: number): Promise<ApiResponse<RevenueStreamSummary>> {
    return apiClient.get<RevenueStreamSummary>(`${this.streamBaseUrl}/${streamId}/summary`);
  }

  // Revenue Target endpoints
  async getRevenueTargets(streamId?: number, page?: number): Promise<ApiResponse<PaginatedResponse<RevenueTarget>>> {
    const params = new URLSearchParams();
    if (streamId) params.append('stream_id', streamId.toString());
    if (page) params.append('page', page.toString());
    return apiClient.get<PaginatedResponse<RevenueTarget>>(`${this.targetBaseUrl}?${params.toString()}`);
  }

  async getRevenueTarget(id: number): Promise<ApiResponse<RevenueTarget>> {
    return apiClient.get<RevenueTarget>(`${this.targetBaseUrl}/${id}`);
  }

  async createRevenueTarget(data: CreateRevenueTargetData): Promise<ApiResponse<RevenueTarget>> {
    return apiClient.post<RevenueTarget>(this.targetBaseUrl, data);
  }

  async updateRevenueTarget(id: number, data: Partial<CreateRevenueTargetData>): Promise<ApiResponse<RevenueTarget>> {
    return apiClient.put<RevenueTarget>(`${this.targetBaseUrl}/${id}`, data);
  }

  async deleteRevenueTarget(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.targetBaseUrl}/${id}`);
  }

  async getTargetsByLocation(
    streamId: number,
    locationType: 'subcounty' | 'ward' | 'zone',
    locationId: number
  ): Promise<ApiResponse<RevenueTarget[]>> {
    return apiClient.get<RevenueTarget[]>(
      `${this.streamBaseUrl}/${streamId}/targets/${locationType}/${locationId}`
    );
  }

  async getWardCollections(streamId: number): Promise<ApiResponse<Record<number, number>>> {
    return apiClient.get<Record<number, number>>(`${this.streamBaseUrl}/${streamId}/ward-collections`);
  }

  // Finance Dashboard Overview
  async getFinanceOverview(periodId?: number): Promise<ApiResponse<any>> {
    const params = periodId ? `?period_id=${periodId}` : '';
    return apiClient.get<any>(`/admin/county/finance${params}`);
  }

  // Finance Reports
  async getFinanceReports(periodId?: number): Promise<ApiResponse<any>> {
    const params = periodId ? `?period_id=${periodId}` : '';
    return apiClient.get<any>(`/admin/county/finance/reports${params}`);
  }
}

export const revenueService = new RevenueService();
export default revenueService;
